import { dynamicPageVariantsColRef } from '@/services/DynamicPageVariantsService';
import { quizFormsQuery } from '@/services/QuizService';
import {
  CachedQuizEmbedded,
  DynamicPage,
  DynamicPageVariant,
  GlobalSection,
  LandingPageAgentSession,
  QuizForm,
  Website,
  WebsiteDataWithForms,
  WebsitePreviewEvent,
  WebsitePreviewEventGenerateSuggestions,
} from '@superfunnel/types';
import { applyExtraData } from '@superfunnel/utils/website';
import {
  doc,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAISuggestionsContext } from '../../../contexts/AISuggestionsContextProvider';
import { useDashboardContext } from '../../../contexts/DashboardContext';
import { UserContext } from '../../../contexts/UserContext';
import { db, functions } from '../../../Firebase';
import { emptyConverter, PREVIEW_ROOT } from '../../../utils';

export interface UseEditorDataReturn {
  /**
   * The raw website data fetched from Firestore, before applying agent session changes.
   */
  rawWebsite?: Website;
  handleGetDataEvent: (data: WebsitePreviewEvent) => void;
  handleGenerateSuggestionsEvent: (
    data: WebsitePreviewEventGenerateSuggestions,
  ) => void;
  sendMessageToIframe: (message: any) => void;
}

interface GlobalSectionUseEditorDataProps {
  mode: 'globalSection';
  globalSectionId: string;
}

interface WebsiteUseEditorProps {
  mode: 'website';
  dynamicPageId: string;
  websiteId: string;
  dynamicPage: DynamicPage | undefined;
  agentSessionData: LandingPageAgentSession | null;
  setDynamicPageVariants: (
    variants: QueryDocumentSnapshot<DynamicPageVariant>[] | undefined,
  ) => void;
}

type UseEditorDataProps = (
  | GlobalSectionUseEditorDataProps
  | WebsiteUseEditorProps
) & {
  ready: number;
  id: string;
  setWebsite: (website: Website | undefined) => void;
  /** Setter for unpublished changes state (for GlobalSection mode) */
  setUnpublishedChanges: (hasChanges: boolean) => void;
};

export function useEditorData({
  ready,
  id,
  setWebsite,
  setUnpublishedChanges,
  ...props
}: UseEditorDataProps): UseEditorDataReturn {
  const { selectedAccount } = useContext(UserContext);
  const { accountBrand, accountConfig, forms, globalSections, quizzes } =
    useDashboardContext();
  const { fieldSuggestions } = useAISuggestionsContext();
  const [rawWebsite, setRawWebsite] = useState<Website | undefined>(undefined);
  const [quizForms, setQuizForms] = useState<
    Record<string, QueryDocumentSnapshot<QuizForm>[]>
  >({});

  // Track when the last agent session change was completed
  const lastChangeCompletedAt = useRef<number | null>(null);

  const partialWebsiteId = useMemo(() => {
    return props.mode === 'website' ? props.websiteId : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).websiteId]);

  const partialDynamicPageId = useMemo(() => {
    return props.mode === 'website' ? props.dynamicPageId : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).dynamicPageId]);

  const partialDynamicPage = useMemo(() => {
    return props.mode === 'website' ? props.dynamicPage : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).dynamicPage]);

  const partialAgentSessionData = useMemo(() => {
    return props.mode === 'website' ? props.agentSessionData : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).agentSessionData]);

  const partialGlobalSectionId = useMemo(() => {
    return props.mode === 'globalSection' ? props.globalSectionId : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).globalSectionId]);

  const setDynamicPageVariants = useMemo(() => {
    return props.mode === 'website' ? props.setDynamicPageVariants : () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, (props as any).setDynamicPageVariants]);

  const sendMessageToIframe = useCallback(
    (message: any) => {
      const iframe = document.getElementById(id) as HTMLIFrameElement;
      if (iframe) {
        iframe.contentWindow?.postMessage(
          JSON.stringify(message),
          PREVIEW_ROOT,
        );
      }
    },
    [id],
  );

  const handleGetDataEvent = useCallback(
    (data: WebsitePreviewEvent) => {
      if (!selectedAccount) return;
      if (props.mode !== 'website') return;

      const getSectionVariants = httpsCallable<any, any>(
        functions,
        'accounts/websites/getSectionVariants',
      );

      getSectionVariants({
        accountId: selectedAccount?.id,
        campaignId: partialDynamicPageId,
        ...(props.mode === 'website' ? { websiteId: partialWebsiteId } : {}),
      }).then((result) => {
        sendMessageToIframe({
          requestId: (data as any).requestId,
          eventName: 'sendData',
          sendDataType: 'sectionVariants',
          section: {},
          sectionTypes: result.data.sectionTypes,
          favoriteSections: result.data.favoriteSections,
          globalSections: result.data.globalSections,
          isSystemAdmin: false, // TODO: Get from auth context
        });
      });
    },
    [
      selectedAccount,
      props.mode,
      partialDynamicPageId,
      partialWebsiteId,
      sendMessageToIframe,
    ],
  );

  const handleGenerateSuggestionsEvent = useCallback(
    (data: WebsitePreviewEventGenerateSuggestions) => {
      fieldSuggestions(
        partialWebsiteId ?? '',
        data.data.sectionId,
        data.data.propKey,
        data.data.fullLine,
        data.data.textSelection,
        data.data.prompt,
      )
        .then((result) => {
          sendMessageToIframe({
            eventName: 'returnGenerateSuggestions',
            requestId: data.data.requestId,
            suggestions: result,
          });
        })
        .catch((e) => {
          sendMessageToIframe({
            eventName: 'returnGenerateSuggestions',
            requestId: data.data.requestId,
            error: e,
          });
        });
    },
    [fieldSuggestions, partialWebsiteId, sendMessageToIframe],
  );

  // Fetch website data
  useEffect(() => {
    if (props.mode !== 'website') return;
    if (
      !selectedAccount ||
      !partialDynamicPageId ||
      !partialWebsiteId ||
      !forms ||
      !globalSections ||
      !ready
    ) {
      return;
    }

    const websiteRef = doc(
      db,
      'accounts',
      selectedAccount.id,
      'campaigns',
      partialDynamicPageId,
      'websites',
      partialWebsiteId,
    ).withConverter(emptyConverter<Website>());

    const unsubscribe = onSnapshot(websiteRef, (website) => {
      if (!website.exists()) return;

      setRawWebsite(website.data());
    });

    return () => {
      unsubscribe();
    };
  }, [
    accountBrand,
    accountConfig,
    forms,
    globalSections,
    partialWebsiteId,
    partialDynamicPage,
    partialDynamicPageId,
    props.mode,
    ready,
    selectedAccount,
  ]);

  // Fetch global section data
  useEffect(() => {
    if (!selectedAccount || !ready) return;
    if (props.mode !== 'globalSection') return;

    const globalSectionRef = doc(
      selectedAccount.ref,
      'globalSections',
      partialGlobalSectionId ?? '',
    ).withConverter(emptyConverter<GlobalSection>());

    const unsubscribe = onSnapshot(globalSectionRef, (globalSection) => {
      if (!globalSection.exists()) return;

      // Track unpublished changes for GlobalSection
      setUnpublishedChanges(globalSection.data().changesToPublish ?? false);

      const website: Website = {
        name: globalSection.data().name,
        createdBy: '',
        createdAt: undefined,
        type: 'short',
        development: {
          ...{ globalEditor: true },
          sections: [globalSection.data().development],
        },
        status: 'draft',
      };

      setRawWebsite(website);
    });

    return () => {
      unsubscribe();
    };
  }, [
    accountBrand,
    accountConfig,
    forms,
    partialGlobalSectionId,
    props.mode,
    ready,
    selectedAccount,
  ]);

  // Subscribe to quiz forms for each quiz
  useEffect(() => {
    if (!selectedAccount || !quizzes || quizzes.length === 0) {
      setQuizForms({});
      return;
    }

    const unsubscribes: (() => void)[] = [];

    for (const quizDoc of quizzes) {
      const unsubscribe = onSnapshot(
        quizFormsQuery(selectedAccount.id, quizDoc.id),
        (snapshot) => {
          setQuizForms((prev) => ({
            ...prev,
            [quizDoc.id]: snapshot.docs,
          }));
        },
      );
      unsubscribes.push(unsubscribe);
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [selectedAccount, quizzes]);

  useEffect(() => {
    if (!rawWebsite || !globalSections || !forms) {
      return;
    }

    if (props.mode === 'website') {
      if (!partialDynamicPage) return;
    }

    // Check if there are any pending changes in the agent session
    const hasAgentSessionChanges = Object.values(
      partialAgentSessionData?.changes || {},
    ).some((change) => change.status === 'pending');

    // Update timestamp when changes complete
    if (!hasAgentSessionChanges && partialAgentSessionData?.websiteData) {
      if (lastChangeCompletedAt.current === null) {
        lastChangeCompletedAt.current = Date.now();
      }
    } else if (hasAgentSessionChanges) {
      // Reset timestamp when new changes appear
      lastChangeCompletedAt.current = null;
    }

    // Use agent session data if:
    // 1. There are active pending changes, OR
    // 2. Changes completed less than 2 seconds ago
    const timeSinceCompletion = lastChangeCompletedAt.current
      ? Date.now() - lastChangeCompletedAt.current
      : Infinity;
    const shouldUseAgentSessionData =
      partialAgentSessionData?.websiteData &&
      (hasAgentSessionChanges || timeSinceCompletion < 2000);

    let currentWebsite = rawWebsite;

    if (shouldUseAgentSessionData) {
      console.log(
        '🤖 Using agent session data',
        hasAgentSessionChanges
          ? 'with pending changes'
          : `(${Math.round(2000 - timeSinceCompletion)}ms remaining)`,
      );
      // Create a mock website structure using agent data
      const fakeWebsite = {
        ...rawWebsite,
        development: {
          ...partialAgentSessionData!.websiteData!,
          sections: partialAgentSessionData!.websiteData!.sections || [],
        },
      };

      currentWebsite = fakeWebsite;
    }

    const enrichedWebsite = applyExtraData(
      currentWebsite,
      partialDynamicPage,
      accountBrand,
      accountConfig,
      globalSections.map((gs) => ({ ...gs.data().production, id: gs.id })),
    );

    enrichedWebsite.development.formIds =
      forms.map((f) => f.id) || enrichedWebsite.development.formIds;

    // Cast to WebsiteDataWithForms since we're enriching with forms at runtime
    (enrichedWebsite.development as WebsiteDataWithForms).forms =
      Object.fromEntries(forms?.map((f) => [f.id, f.data()]));

    // Enrich with quizzes (build CachedQuizEmbedded structure)
    if (quizzes && quizzes.length > 0) {
      const enrichedQuizzes: Record<string, CachedQuizEmbedded> = {};
      for (const quizDoc of quizzes) {
        const quiz = quizDoc.data();
        const quizId = quizDoc.id;
        const quizFormDocs = quizForms[quizId] || [];

        enrichedQuizzes[quizId] = {
          quizId,
          formIdsWeights: quiz.formIdsWeights || {},
          forms: Object.fromEntries(quizFormDocs.map((f) => [f.id, f.data()])),
        };
      }
      (enrichedWebsite.development as WebsiteDataWithForms).quizIds =
        quizzes.map((q) => q.id) ||
        (enrichedWebsite.development as WebsiteDataWithForms).quizIds ||
        [];
      (enrichedWebsite.development as WebsiteDataWithForms).quizzes =
        enrichedQuizzes;
    }

    setWebsite(enrichedWebsite);

    sendMessageToIframe({
      eventName: 'websiteUpdated',
      website: enrichedWebsite.development,
    });
  }, [
    accountBrand,
    accountConfig,
    forms,
    globalSections,
    partialAgentSessionData,
    partialDynamicPage,
    props.mode,
    quizForms,
    quizzes,
    rawWebsite,
    sendMessageToIframe,
    setWebsite,
  ]);

  // Fetch dynamic page variants
  useEffect(() => {
    if (!selectedAccount || !partialDynamicPageId || props.mode !== 'website') {
      setDynamicPageVariants(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = partialDynamicPageId;

    const variantsColRef = dynamicPageVariantsColRef(accountId, dynamicPageId);
    const unsubscribe = onSnapshot(
      query(
        variantsColRef,
        where('status', 'in', ['draft', 'active', 'disabled']),
        orderBy('createdAt', 'asc'),
      ),
      (variants) => {
        setDynamicPageVariants(variants.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [
    partialDynamicPageId,
    props.mode,
    selectedAccount,
    setDynamicPageVariants,
  ]);

  return {
    rawWebsite,
    handleGetDataEvent,
    handleGenerateSuggestionsEvent,
    sendMessageToIframe,
  };
}
